import { css } from '@emotion/css';
import { useState } from 'react';

import { type GrafanaTheme2 } from '@grafana/data';
import { t } from '@grafana/i18n';
import { Alert, Button, Modal, useStyles2 } from '@grafana/ui';

import { useQueryLibraryContext } from '../../../features/explore/QueryLibrary/QueryLibraryContext';

import { SavedQueriesLayout } from './SavedQueriesLayout';

const FEEDBACK_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLScHILHDfIJG8ChaU42AWoGVBR78hIwvvUE-15_A0G5miHgvhQ/viewform?usp=header';

const FEEDBACK_DISMISSED_KEY = 'savedQueriesFeedbackDismissed';

// Top-level modal shell: renders the query layout with an optional dismissible feedback banner.
export function SavedQueriesModal() {
  const { isDrawerOpen, closeDrawer } = useQueryLibraryContext();
  const styles = useStyles2(getStyles);
  const [isFeedbackDismissed, setIsFeedbackDismissed] = useState(
    () => localStorage.getItem(FEEDBACK_DISMISSED_KEY) === 'true'
  );

  // Feedback button will be removed shortly after receiving feedback after launch.
  const openFeedback = () => window.open(FEEDBACK_URL, '_blank');

  const modalTitle = (
    <div className={styles.titleRow}>
      <h2 className={styles.titleText}>{t('saved-queries.modal.title', 'Saved queries')}</h2>
      {isFeedbackDismissed && (
        <Button
          fill="text"
          variant="secondary"
          icon="comment-alt-message"
          onClick={openFeedback}
          className={styles.feedbackButton}
        >
          {t('saved-queries.feedback.button', 'Give feedback')}
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      ariaLabel={t('saved-queries.modal.aria-label', 'Saved queries')}
      isOpen={isDrawerOpen}
      onDismiss={() => closeDrawer(false)}
      className={styles.modal}
      contentClassName={styles.content}
    >
      {!isFeedbackDismissed && (
        <div className={styles.feedbackAlertContainer}>
          <Alert
            severity="info"
            title={t(
              'saved-queries.feedback.alert-text',
              'We recently redesigned Saved queries and would love your thoughts!'
            )}
            action={
              <Button variant="secondary" onClick={openFeedback}>
                {t('saved-queries.feedback.button', 'Give feedback')}
              </Button>
            }
            onRemove={() => {
              localStorage.setItem(FEEDBACK_DISMISSED_KEY, 'true');
              setIsFeedbackDismissed(true);
            }}
          />
        </div>
      )}
      <SavedQueriesLayout />
    </Modal>
  );
}

const getStyles = (theme: GrafanaTheme2) => ({
  modal: css({
    width: '90vw',
    maxWidth: '1400px',
    '& [class*="modalHeader"]': {
      '> div:last-child': {
        flexGrow: 0,
        marginLeft: theme.spacing(2),
      },

      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(2),
    },
  }),
  content: css({
    display: 'flex',
    flexDirection: 'column',
    height: '70vh',
    overflow: 'hidden',
    padding: 0,
  }),
  titleRow: css({
    display: 'flex',
    flex: 1,
    alignItems: 'center',
  }),
  feedbackButton: css({
    marginLeft: 'auto',
  }),
  titleText: css({
    fontSize: theme.typography.size.lg,
    margin: theme.spacing(0, 4, 0, 1),
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    top: '2px',
  }),
  feedbackAlertContainer: css({
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    borderBottom: `1px solid ${theme.colors.border.weak}`,
  }),
});
